import { useEffect, RefObject } from 'react';

/**
 * Enables Enter-key navigation in a data-entry grid.
 *
 * Attaches a single capture-phase listener to `document` so it works even
 * inside MUI Dialogs (which mount lazily — the container ref is null until
 * the dialog opens). The listener reads containerRef.current at event time,
 * so it always targets the correct DOM node regardless of mount timing.
 *
 * Behaviour:
 * - Enter inside any text/number input → focus the next input in the grid
 * - Enter on a button, checkbox, radio → ignored (native behaviour kept)
 * - Enter while an Autocomplete dropdown is open → ignored (let MUI select)
 */
export function useGridEnterNavigation(containerRef: RefObject<HTMLElement | null>) {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Enter') return;

      // Read the ref at event time — works even after Dialog mounts lazily
      const container = containerRef.current;
      if (!container) return;

      const target = e.target as HTMLElement;

      // Only act on events that originated inside our grid container
      if (!container.contains(target)) return;

      // Skip buttons
      if (target.tagName === 'BUTTON') return;

      // Skip non-text input types
      if (target.tagName === 'INPUT') {
        const t = (target as HTMLInputElement).type.toLowerCase();
        if (['checkbox', 'radio', 'submit', 'reset', 'button', 'file', 'color', 'range'].includes(t)) return;
      }

      // If any Autocomplete / Select listbox is currently open, let MUI handle it
      if (document.querySelector('[role="listbox"]')) return;

      // Collect all navigable inputs inside the container (text, number, search, etc.)
      const inputs = Array.from(
        container.querySelectorAll<HTMLInputElement>(
          'input:not([disabled]):not([readonly])' +
          ':not([type="hidden"]):not([type="checkbox"]):not([type="radio"])' +
          ':not([type="button"]):not([type="submit"]):not([type="reset"])' +
          ':not([type="file"]):not([type="color"]):not([type="range"])'
        )
      ).filter(el => el.offsetParent !== null); // visible only

      const idx = inputs.indexOf(target as HTMLInputElement);
      if (idx === -1) return;

      e.preventDefault();

      const next = inputs[idx + 1];
      if (next) {
        next.focus();
        // Defer select() so MUI can finish its own focus handling first
        setTimeout(() => next.select(), 0);
      }
    };

    // Capture phase fires BEFORE React synthetic events and MUI internal handlers
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  // Empty deps intentional: we read containerRef.current at event time, not at effect time
}
